import React from "react";
import { useData, useLayout } from "@src/hooks/redux";
import type { AuthService, TradingDroidApi } from "@tradingdroid/api-client";
import { createApiClient } from "@tradingdroid/api-client";

export type { JWTPayload, TokenData, User } from "@tradingdroid/api-client";
export { TokenManager } from "@tradingdroid/api-client";

// Global API instances (initialized once, then accessed directly)
let _tdApi: TradingDroidApi | null = null;
let _authService: AuthService | null = null;

/**
 * Hook to initialize API clients
 * Returns initialization state and provides direct access to initialized APIs
 *
 * Usage:
 * ```tsx
 * const { isLoading, error, data } = useInitializeApi();
 * // data is null while loading, contains { tdApi, authService } when ready
 * ```
 */
export const useInitializeApi = () => {
  const { resetData } = useData();
  const { resetLayoutState } = useLayout();

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    // Skip if already initialized
    if (_tdApi) {
      setIsLoading(false);
      return;
    }

    const initializeApi = async () => {
      // Empty PUBLIC_TD_API_HOST means "same origin" — useful when the
      // dev/preview server proxies /v1/* and the app is reached via a
      // tunnel (so the API base must match wherever the page is loaded
      // from). Fall back to window.location.origin in that case so the
      // api-client gets a valid absolute baseUrl.
      const apiHostEnv = import.meta.env.PUBLIC_TD_API_HOST ?? "";
      const apiHost = apiHostEnv || window.location.origin;
      const basicAuth = import.meta.env.PUBLIC_TD_API_AUTH;

      if (!apiHost) {
        const err = new Error("API host configuration is missing");
        console.error("PUBLIC_TD_API_HOST environment variable is not defined");
        setError(err);
        setIsLoading(false);
        return;
      }

      try {
        // Create API client with auth callbacks
        const { api: apiClient, authService } = createApiClient(apiHost, basicAuth, 30000, {
          authCallbacks: {
            onLogout: async () => {
              // Clear Redux state
              resetData();
              resetLayoutState();
              console.log("[LOGOUT] Cleared Redux state");
            },
            onTokenRefreshFailed: async (error: Error) => {
              console.error("Token refresh failed:", error);
              // Hard redirect to login — clears all React state and forces re-authentication
              window.location.href = "/login";
            },
            onError: async (error: Error) => {
              console.error("Authentication error:", error);
            },
          },
        });

        // Initialize API clients
        _tdApi = apiClient;
        _authService = authService;

        console.log(`API client ready (${basicAuth ? "Basic Auth" : "JWT"})`);
        setIsLoading(false);
      } catch (err) {
        console.error("Error during API initialization:", err);
        setError(err as Error);
        setIsLoading(false);
      }
    };

    initializeApi();
  }, [resetData, resetLayoutState]);

  // Only return data when successfully initialized (not loading and no error)
  const data =
    !isLoading && !error
      ? {
          tdApi: _tdApi,
          authService: _authService,
        }
      : null;

  return {
    data,
    isLoading,
    error,
  };
};

/**
 * Proxy exports for convenient access to API instances
 * These work after useInitializeApi has run in App.tsx
 *
 * For new code: Prefer getting these from useInitializeApi hook which provides initialization state
 * For existing code: These Proxies work fine and throw clear errors if accessed before initialization
 */
export const tdApi = new Proxy({} as TradingDroidApi, {
  get(_target, prop) {
    if (!_tdApi) {
      throw new Error("API not initialized. Ensure useInitializeApi hook has run in App.tsx");
    }
    const value = _tdApi[prop as keyof TradingDroidApi];
    return typeof value === "function" ? value.bind(_tdApi) : value;
  },
});

export const authService = new Proxy({} as AuthService, {
  get(_target, prop) {
    if (!_authService) {
      throw new Error("API not initialized. Ensure useInitializeApi hook has run in App.tsx");
    }
    const value = _authService[prop as keyof AuthService];
    return typeof value === "function" ? value.bind(_authService) : value;
  },
});
